/**
 * Playback Capability Probes
 *
 * Answers whether the browser will actually produce sound right now, for each
 * output path independently. The two paths are gated separately: a browser can
 * refuse speech synthesis while playing audio elements normally.
 */

import { SILENT_AUDIO_SRC } from './audio-unlock';

/**
 * Speech synthesis capability.
 *
 * `unusable` is a browser that accepts an utterance and never starts it. A
 * user gesture cannot fix that state; it needs a different engine.
 */
export type SpeechCapability = 'allowed' | 'blocked' | 'unusable' | 'unknown';

/** Audio element playback capability. */
export type AudioCapability = 'allowed' | 'blocked' | 'unknown';

/** Combined probe result. */
export interface PlaybackCapabilities {
  speech: SpeechCapability;
  audio: AudioCapability;
}

/** How long an accepted utterance may take to start before it counts as unusable. */
const SPEECH_PROBE_TIMEOUT_MS = 1200;

/**
 * Probe speech synthesis with a silent utterance.
 *
 * @param timeoutMs - Start deadline before the engine is judged unusable
 * @returns The capability verdict
 */
export function probeSpeechSynthesis(
  timeoutMs: number = SPEECH_PROBE_TIMEOUT_MS
): Promise<SpeechCapability> {
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    return Promise.resolve('unknown');
  }

  // Never enqueue without user activation. An unactivated utterance fails
  // `not-allowed`, and on some Chromium variants (observed: Vivaldi) that
  // failure wedges the speech queue for the page's lifetime; cancel() does
  // not clear it. The verdict is unknowable pre-gesture anyway.
  if (window.navigator?.userActivation?.hasBeenActive !== true) {
    return Promise.resolve('unknown');
  }

  const synth = window.speechSynthesis;

  return new Promise<SpeechCapability>((resolve) => {
    let settled = false;
    const finish = (result: SpeechCapability): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };

    const utterance = new SpeechSynthesisUtterance(' ');
    utterance.volume = 0;
    utterance.onstart = () => finish('allowed');
    utterance.onend = () => finish('allowed');
    utterance.onerror = (event: SpeechSynthesisErrorEvent) =>
      finish(event.error === 'not-allowed' ? 'blocked' : 'unknown');

    // No synth.cancel() here: it is global, and real speech may have started
    // while the probe was pending. The timeout only reports the verdict.
    const timer = setTimeout(() => finish('unusable'), timeoutMs);

    try {
      synth.speak(utterance);
    } catch {
      finish('unknown');
    }
  });
}

/**
 * Probe audio element playback with a silent clip.
 *
 * @returns The capability verdict
 */
export async function probeAudioPlayback(): Promise<AudioCapability> {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') {
    return 'unknown';
  }

  const policy = (
    navigator as Navigator & {
      getAutoplayPolicy?: (type: string) => string;
    }
  ).getAutoplayPolicy;
  if (typeof policy === 'function') {
    const verdict = policy.call(navigator, 'mediaelement');
    if (verdict === 'allowed') return 'allowed';
    if (verdict === 'disallowed' || verdict === 'allowed-muted') return 'blocked';
  }

  const audio = new Audio(SILENT_AUDIO_SRC);
  audio.volume = 0;
  try {
    await audio.play();
    audio.pause();
    return 'allowed';
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    return name === 'NotAllowedError' ? 'blocked' : 'unknown';
  }
}

/**
 * Probe both output paths concurrently.
 *
 * @returns Capabilities for speech synthesis and audio playback
 */
export async function probePlaybackCapabilities(): Promise<PlaybackCapabilities> {
  const [speech, audio] = await Promise.all([probeSpeechSynthesis(), probeAudioPlayback()]);
  return { speech, audio };
}

/**
 * Whether a user gesture would change anything.
 *
 * Only a `blocked` path is recoverable by interaction; `unusable` needs a
 * different engine and `allowed` needs nothing.
 *
 * @param capabilities - Probe results, or null when not yet known
 * @returns True when prompting could unblock playback
 */
export function gestureWouldHelp(capabilities: PlaybackCapabilities | null): boolean {
  if (!capabilities) return true;
  return capabilities.speech === 'blocked' || capabilities.audio === 'blocked';
}
